var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _Component_instances, updateRefs_fn, _mutationObserver, _isDescendant;
import { D as DeclarativeShadowElement } from "./critical-Da0Dmhh9.js";
import { r as requestIdleCallback } from "./utilities-2Suih3Nm.js";
class Component extends DeclarativeShadowElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _Component_instances);
    /**
     * An object holding references to child elements with `ref` attributes.
     *
     * @type {RefsType<T>}
     */
    __publicField(
      this,
      "refs",
      /** @type {RefsType<T>} */
      {}
    );
    /**
     * An array of required refs. If a ref is not found, an error will be thrown.
     *
     * @type {string[] | undefined}
     */
    __publicField(this, "requiredRefs");
    /**
     * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
     *
     * @type {MutationObserver}
     */
    __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
      if (mutations.some(
        (m) => m.type === "attributes" && __privateGet(this, _isDescendant).call(this, m.target) || m.type === "childList" && [...m.addedNodes, ...m.removedNodes].some(__privateGet(this, _isDescendant))
      )) {
        __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
      }
    }));
    /**
     * Checks if a given node is a descendant of this component.
     *
     * @param {Node} node - The node to check.
     * @returns {boolean} True if the node is a descendant of this component.
     */
    __privateAdd(this, _isDescendant, (node) => getClosestComponent(getAncestor(node)) === this);
  }
  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }
  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();
    __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
    requestIdleCallback(() => {
      for (const root of this.roots) {
        __privateGet(this, _mutationObserver).observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["ref"],
          attributeOldValue: true
        });
      }
    });
  }
  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    __privateGet(this, _mutationObserver).takeRecords();
    __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
  }
  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    __privateGet(this, _mutationObserver).disconnect();
  }
}
_Component_instances = new WeakSet();
/**
 * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
 *
 * This method is called to keep the `refs` object in sync with the DOM.
 */
updateRefs_fn = function() {
  var _a;
  const refs = (
    /** @type any */
    {}
  );
  const elements = this.roots.reduce(
    (acc, root) => {
      for (const element of root.querySelectorAll("[ref]")) {
        if (!__privateGet(this, _isDescendant).call(this, element)) continue;
        acc.add(element);
      }
      return acc;
    },
    /** @type {Set<Element>} */
    /* @__PURE__ */ new Set()
  );
  for (const ref of elements) {
    const refName = ref.getAttribute("ref") ?? "";
    const isArray = refName.endsWith("[]");
    const path = isArray ? refName.slice(0, -2) : refName;
    if (isArray) {
      const array = Array.isArray(refs[path]) ? refs[path] : [];
      array.push(ref);
      refs[path] = array;
    } else {
      refs[path] = ref;
    }
  }
  if ((_a = this.requiredRefs) == null ? void 0 : _a.length) {
    for (const ref of this.requiredRefs) {
      if (!(ref in refs)) {
        throw new MissingRefError(ref, this);
      }
    }
  }
  this.refs = /** @type {RefsType<T>} */
  refs;
};
_mutationObserver = new WeakMap();
_isDescendant = new WeakMap();
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;
  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;
  return null;
}
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith("-component")) return node;
  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);
  return null;
}
let initialized = false;
function registerEventListeners() {
  if (initialized) return;
  initialized = true;
  const events = ["click", "change", "select", "focus", "blur", "submit", "input", "keydown", "keyup", "toggle"];
  const shouldBubble = ["focus", "blur"];
  const expensiveEvents = ["pointerenter", "pointerleave"];
  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;
    document.addEventListener(
      eventName,
      (event) => {
        var _a;
        const element = getElement(event);
        if (!element) return;
        const proxiedEvent = event.target !== element ? new Proxy(event, {
          get(target, property) {
            if (property === "target") return element;
            const value2 = Reflect.get(target, property);
            if (typeof value2 === "function") {
              return value2.bind(target);
            }
            return value2;
          }
        }) : event;
        const value = element.getAttribute(attribute) ?? "";
        let [selector, method] = value.split("/");
        const data = (_a = value.match(new RegExp("(?<=[\\/\\?][^\\/\\?]+)[\\/\\?][^\\/\\?]+$"))) == null ? void 0 : _a[0];
        const instance = selector ? selector.startsWith("#") ? document.querySelector(selector) : element.closest(selector) : getClosestComponent(element);
        if (!(instance instanceof Component) || !method) return;
        method = method.replace(/\?.*/, "");
        const callback = (
          /** @type {any} */
          instance[method]
        );
        if (typeof callback === "function") {
          try {
            const args = [proxiedEvent];
            if (data) args.unshift(parseData(data));
            callback.call(instance, ...args);
          } catch (error) {
          }
        }
      },
      { capture: true }
    );
  }
  function getElement(event) {
    var _a;
    const target = ((_a = event.composedPath) == null ? void 0 : _a.call(event)[0]) ?? event.target;
    if (!(target instanceof Element)) return;
    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }
    if (expensiveEvents.includes(event.type)) {
      return null;
    }
    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);
  return delimiter === "?" ? Object.fromEntries(
    Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
  ) : parseValue(data);
}
function parseValue(str) {
  if (str === "true") return true;
  if (str === "false") return false;
  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== "") return maybeNumber;
  return str;
}
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}
export {
  Component as C
};
